import React, { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { EstablishmentManager } from "@/components/EstablishmentManager";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { SaveAsPlaceButton } from "@/components/SaveAsPlaceButton";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger, ContextMenuLabel } from "@/components/ui/context-menu";
import { Zap, Trash2, Loader2, MapPin, Send, AlertTriangle, CheckCircle2, RefreshCw, Repeat, Plus, FileUp, Pencil, Sparkles, Download } from "lucide-react";
import { toast } from "sonner";
import { RegulReport } from "@/components/RegulReport";
import { VEHICLE_TYPES, DESTINATIONS, STATUS_META, today } from "@/lib/constants";
import { EntityLink } from "@/components/EntityLink";
import { useLiveSync } from "@/hooks/useLiveSync";

export default function Jeregul() {
  const [date, setDate] = useState(today());
  const [rides, setRides] = useState([]);
  const [regulating, setRegulating] = useState(false);

  // form state
  const [patient, setPatient] = useState(null);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [showMatches, setShowMatches] = useState(false);
  const [destIdx, setDestIdx] = useState("0");
  const [sens, setSens] = useState("aller"); // aller: domicile->etab ; retour: etab->domicile ; aller-retour: les deux
  const [time, setTime] = useState("08:00");
  const [returnTime, setReturnTime] = useState("");
  const [type, setType] = useState("VSL");
  const timeRef = useRef(null);
  const patientRef = useRef(null);
  const importRef = useRef(null);
  const [importing, setImporting] = useState(false);

  // Courses en série (RDV périodiques)
  const [serie, setSerie] = useState(false);
  const [serieFreq, setSerieFreq] = useState("weekly"); // daily | weekly | weekdays
  const [serieCount, setSerieCount] = useState(4);
  const [serieRegul, setSerieRegul] = useState(true);

  const load = () => api.get("/rides", { params: { date } }).then((r) => setRides(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);
  useLiveSync(() => load());

  const [tribus, setTribus] = useState([]);
  const [pushRide, setPushRide] = useState(null);
  const [regulResult, setRegulResult] = useState(null);
  const [report, setReport] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [vehCount, setVehCount] = useState(0);
  useEffect(() => { api.get("/pro-tribus").then((r) => setTribus(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehCount((r.data || []).filter((v) => v.active !== false).length)).catch(() => {});
  }, []);

  // Établissements de santé (liste du gérant ; repli sur les destinations par défaut si vide).
  const [estabs, setEstabs] = useState(DESTINATIONS);
  const [showEstab, setShowEstab] = useState(false);
  const [destAddr, setDestAddr] = useState(null);   // destination libre (tourisme/privé)
  const [destQuery, setDestQuery] = useState("");
  const loadEstabs = () => api.get("/establishments").then((r) => {
    const list = (r.data || []).map((e) => ({ label: e.name, lat: e.lat, lon: e.lon, address: e.address || e.name, favorite: !!e.favorite }));
    const finalList = list.length ? list : DESTINATIONS;
    setEstabs(finalList);
    const favIdx = finalList.findIndex((e) => e.favorite);
    if (favIdx >= 0) setDestIdx(String(favIdx));
  }).catch(() => {});
  useEffect(() => { loadEstabs(); }, []);
  useEffect(() => { if (Number(destIdx) >= estabs.length) setDestIdx("0"); }, [estabs]); // eslint-disable-line

  const onRest = drivers.filter((d) => (d.rest_dates || []).includes(date)).length;
  const availableDrivers = drivers.filter((d) => d.status === "disponible" && !(d.rest_dates || []).includes(date)).length;
  const understaffed = vehCount > 0 && availableDrivers < vehCount;

  const pushAllSurcharge = async (tid) => {
    try {
      const { data } = await api.post("/rides/push-surcharge", { date, tribu_id: tid });
      toast.success(`${data.pushed} course(s) poussée(s) sur « ${data.tribu} » (anonymisées)`);
      setRegulResult(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const pushToBourse = async (tid) => {
    try {
      await api.post(`/rides/${pushRide.id}/push-bourse`, { tribu_id: tid });
      toast.success("Course poussée sur la bourse d'échange (données anonymisées)");
      setPushRide(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  useEffect(() => {
    if (!query || patient) { setMatches([]); return; }
    const t = setTimeout(() => {
      api.get("/patients", { params: { q: query } }).then((r) => { setMatches(r.data); setShowMatches(true); });
    }, 200);
    return () => clearTimeout(t);
  }, [query, patient]);

  const pickPatient = (p) => {
    setPatient(p);
    setQuery(`${p.prenom} ${p.nom}`);
    setShowMatches(false);
    // Catégorie de véhicule par défaut du patient (sinon dérivée du type sanitaire/privé).
    setType(VEHICLE_TYPES.includes(p.default_vehicle_type) ? p.default_vehicle_type : (p.type === "prive" ? "Taxi" : "VSL"));
    timeRef.current?.focus();
  };

  const resetForm = () => { setPatient(null); setQuery(""); setMatches([]); };

  const focusForm = () => {
    patientRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => patientRef.current?.focus(), 250);
  };

  const [importJob, setImportJob] = useState(null);
  const [importSel, setImportSel] = useState([]);
  const [confirming, setConfirming] = useState(false);

  const handleImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/rides/import-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = data.job_id;
      if (!jobId) throw new Error("Import non démarré");
      toast.message(data.method === "csv" ? "Fichier reconnu — analyse des colonnes (sans IA)…" : "Lecture IA du document lancée…",
        { description: "Vous pourrez vérifier les courses avant leur création." });
      // Polling du statut (le traitement tourne en tâche de fond côté serveur).
      const started = Date.now();
      let job = null;
      while (Date.now() - started < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: st } = await api.get(`/rides/import-document/status/${jobId}`);
        job = st;
        if (st.status !== "processing") break;
      }
      if (!job || job.status === "processing") { toast.error("Import trop long, réessayez avec un document plus court."); return; }
      if (job.status === "error") { toast.error(job.error || "La lecture du document a échoué. Réessayez."); return; }
      const rows = job.proposed || [];
      if (!rows.length) {
        toast.warning("Aucune course exploitable détectée dans le document");
        if (job.skipped?.length) toast.message(`${job.skipped.length} ligne(s) ignorée(s)`, { description: job.skipped.slice(0, 3).map((s) => `${s.patient_name}: ${s.reason}`).join(" · ") });
        return;
      }
      setImportSel(rows.map((_, i) => i));
      setImportJob(job);
    } catch (err) { toast.error(apiError(err)); }
    finally { setImporting(false); }
  };

  const createFiche = async (i) => {
    try {
      const { data } = await api.post(`/rides/import-document/create-patient/${importJob.id}`, { index: i });
      setImportJob((j) => ({ ...j, proposed: j.proposed.map((r, k) => (k === i ? data.row : r)) }));
      toast.success("Fiche patient créée et liée à la course");
    } catch (err) { toast.error(apiError(err)); }
  };

  const downloadTemplate = () => {
    const [y, m, d] = date.split("-");
    const dt = `${d}/${m}/${y}`;
    const csv =
      "Date;Heure RDV;Fin de soin;Nom Patient;Téléphone;Adresse de prise en charge;Destination;Type véhicule;Notes\n" +
      `${dt};09:00;;Jean Dupont;0612345678;12 rue de la Liberté 21000 Dijon;CHU Dijon Bourgogne, Dijon;VSL;Aller consultation\n` +
      `${dt};;11h30;Marie Martin;0698765432;CHU Dijon Bourgogne, Dijon;5 rue Monge 21000 Dijon;Ambulance;Retour - fin de soin 11h30\n` +
      `${dt};14:15;;Paul Bernard;;3 place Darcy 21000 Dijon;Clinique de Fontaine, Fontaine-les-Dijon;Taxi;\n`;
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "modele_import_pacemia.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Modèle CSV téléchargé — remplissez-le ou donnez-le à votre logiciel tiers");
  };

  const confirmImport = async () => {
    if (!importJob) return;
    setConfirming(true);
    try {
      const { data } = await api.post(`/rides/import-document/confirm/${importJob.id}`, { indices: importSel });
      toast.success(`${data.created} course(s) importée(s)${data.grouped_total ? ` · ${data.grouped_total} mutualisée(s)` : ""} et régulée(s)`);
      const firstDate = data.rides?.[0]?.date;
      setImportJob(null);
      if (firstDate && firstDate !== date) setDate(firstDate);
      else load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setConfirming(false); }
  };

  // Génère la liste des dates pour une éventuelle série.
  const buildDates = () => {
    if (!serie) return [date];
    const n = Math.max(1, Math.min(60, Number(serieCount) || 1));
    const base = new Date(date + "T00:00:00");
    const out = [];
    if (serieFreq === "weekdays") {
      const d = new Date(base);
      while (out.length < n) {
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) out.push(d.toISOString().slice(0, 10));
        d.setDate(d.getDate() + 1);
      }
    } else {
      const step = serieFreq === "daily" ? 1 : 7;
      for (let i = 0; i < n; i++) {
        const d = new Date(base);
        d.setDate(base.getDate() + i * step);
        out.push(d.toISOString().slice(0, 10));
      }
    }
    return out;
  };

  const addRide = async (e) => {
    e?.preventDefault();
    if (!patient) { toast.error("Sélectionnez un patient"); return; }
    if (sens === "aller-retour" && !returnTime) { toast.error("Renseignez l'heure de retour"); return; }
    const isSan = type !== "Taxi";
    const dest = isSan ? (estabs[Number(destIdx)] || estabs[0]) : destAddr;
    if (!isSan && (!dest || dest.lat == null)) { toast.error("Renseignez l'adresse de destination"); return; }
    if (patient.lat == null || patient.lon == null) {
      toast.error(`Domicile de ${patient.prenom} ${patient.nom} non géolocalisé — corrigez l'adresse dans sa fiche patient`);
      return;
    }
    if (isSan && (!dest || dest.lat == null)) { toast.error("Établissement de destination non géolocalisé"); return; }
    const home = { address: patient.address, lat: patient.lat, lon: patient.lon };
    const dates = buildDates();
    const mk = (from, to, t, d, careEnd = null) => ({
      date: d, type: type === "Taxi" ? "prive" : "sanitaire", vehicle_type: type,
      patient_id: patient.id,
      patient_name: `${patient.prenom} ${patient.nom}`,
      patient_phone: patient.phone || "",
      notes: "",
      time: t,
      care_end: careEnd,
      pickup_address: from.address, pickup_lat: from.lat, pickup_lon: from.lon,
      dropoff_address: to.address, dropoff_lat: to.lat, dropoff_lon: to.lon,
    });
    try {
      let created = 0;
      for (const d of dates) {
        if (sens === "aller-retour") {
          await api.post("/rides", mk(home, dest, time, d));
          // Retour : l'heure saisie = FIN DE SOIN -> PEC au plus tôt, attente max réglable.
          await api.post("/rides", mk(dest, home, returnTime, d, returnTime));
          created += 2;
        } else {
          const from = sens === "aller" ? home : dest;
          const to = sens === "aller" ? dest : home;
          // Retour simple : l'heure saisie = fin de soin.
          await api.post("/rides", mk(from, to, time, d, sens === "retour" ? time : null));
          created += 1;
        }
      }
      if (serie) toast.success(`Série créée : ${created} course(s) sur ${dates.length} date(s)`);
      else toast.success(sens === "aller-retour" ? "Aller + retour ajoutés au bac d'attente" : "Course ajoutée au bac d'attente");
      resetForm();
      load();
      // Lance la régul sur chaque date de la série si demandé.
      if (serie && serieRegul) {
        let totAssigned = 0, totSurcharge = 0;
        for (const d of dates) {
          try {
            const { data } = await api.post("/rides/regul", { date: d });
            totAssigned += data.assigned || 0;
            totSurcharge += data.surcharge || 0;
          } catch { /* ignore date-level error, continue */ }
        }
        toast.success(`Régul série : ${totAssigned} affectée(s), ${totSurcharge} en surcharge sur ${dates.length} date(s)`);
        load();
      }
    } catch (err) { toast.error(apiError(err)); }
  };

  const removeRide = async (id) => { await api.delete(`/rides/${id}`); load(); };

  const [editRide, setEditRide] = useState(null);
  const openEdit = (r) => setEditRide({
    ...r,
    vehicle_type: r.vehicle_type || (["prive", "tourisme"].includes(r.type) ? "Taxi" : "VSL"),
    pickupQuery: r.pickup_address, dropoffQuery: r.dropoff_address,
  });
  const saveEdit = async () => {
    const r = editRide;
    if (!r.time || !r.pickup_address || !r.dropoff_address) { toast.error("Champs requis manquants"); return; }
    // Blocage strict : l'adresse doit avoir été choisie dans les suggestions (coordonnées fiables).
    if ((r.pickupQuery || "") !== (r.pickup_address || "") || r.pickup_lat == null) {
      toast.error("Adresse de prise en charge : choisissez une proposition dans la liste");
      return;
    }
    if ((r.dropoffQuery || "") !== (r.dropoff_address || "") || r.dropoff_lat == null) {
      toast.error("Adresse de destination : choisissez une proposition dans la liste");
      return;
    }
    try {
      await api.put(`/rides/${r.id}`, {
        date: r.date, time: r.time,
        type: r.vehicle_type === "Taxi" ? "prive" : "sanitaire", vehicle_type: r.vehicle_type,
        patient_id: r.patient_id || null, patient_name: r.patient_name, patient_phone: r.patient_phone || "",
        pickup_address: r.pickup_address, pickup_lat: r.pickup_lat, pickup_lon: r.pickup_lon,
        dropoff_address: r.dropoff_address, dropoff_lat: r.dropoff_lat, dropoff_lon: r.dropoff_lon,
        notes: r.notes || "",
      });
      toast.success("Course modifiée (relancez la régul pour la réaffecter)");
      setEditRide(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const runRegul = async (reset = false) => {
    setRegulating(true);
    try {
      const { data } = await api.post("/rides/regul", { date, reset });
      toast.success(`${reset ? "Recalcul complet" : "RÉGUL"} terminé : ${data.assigned} affectées${data.grouped ? `, ${data.grouped} groupées` : ""}, ${data.surcharge} en surcharge · ${data.empty_km_total} km à vide${data.alerts?.length ? ` · ${data.alerts.length} alerte(s)` : ""}`);
      setRegulResult(data);
      setReport({ date, alerts: data.alerts || [], kpis: data.kpis || {}, meal_breaks: data.meal_breaks || [], chronogram: data.chronogram || [] });
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setRegulating(false); }
  };

  useEffect(() => {
    api.get("/rides/regul-report", { params: { date } })
      .then((r) => setReport(r.data && (r.data.kpis || r.data.ai) ? r.data : null))
      .catch(() => setReport(null));
  }, [date]);

  // Analyse IA du planning (Claude Haiku, économique) : tâche de fond + polling.
  const [aiBusy, setAiBusy] = useState(false);
  const aiTimer = useRef(null);
  useEffect(() => () => aiTimer.current && clearTimeout(aiTimer.current), []);
  const runAiAnalysis = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/rides/ai-analyze", { date });
      const poll = async (tries = 0) => {
        if (tries > 30) { setAiBusy(false); toast.error("L'analyse IA prend trop de temps. Réessayez."); return; }
        try {
          const { data: job } = await api.get(`/rides/ai-analyze/${data.job_id}`);
          if (job.status === "done") {
            setReport((prev) => ({ ...(prev || { date }), ai: job.result }));
            toast.success(`Analyse IA terminée : ${job.result.suggestions?.length || 0} suggestion(s)`);
            setAiBusy(false);
          } else if (job.status === "error") {
            toast.error(job.error || "Analyse IA échouée");
            setAiBusy(false);
          } else {
            aiTimer.current = setTimeout(() => poll(tries + 1), 3000);
          }
        } catch { setAiBusy(false); }
      };
      poll();
    } catch (err) { toast.error(apiError(err)); setAiBusy(false); }
  };

  const pending = rides.filter((r) => !r.assigned_vehicle_id);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Jeregul — Saisie Flash</h1>
          <p className="text-muted-foreground text-sm">Empilez les courses de la journée, puis lancez la régulation IA.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <input
              ref={importRef}
              type="file"
              accept=".pdf,.docx,.txt,.csv,.xlsx"
              className="hidden"
              onChange={handleImport}
              data-testid="jeregul-import-input"
            />
            <Button
              variant="outline"
              onClick={() => importRef.current?.click()}
              disabled={importing}
              className="gap-2"
              data-testid="jeregul-import-button"
              title="Importer des courses depuis un fichier (CSV, Excel, PDF, Word, TXT) — colonnes reconnues automatiquement, IA en secours"
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
              {importing ? "Analyse…" : "Importer un fichier"}
            </Button>
            <button
              type="button"
              onClick={downloadTemplate}
              className="block mx-auto mt-1 text-[11px] text-primary hover:underline flex items-center gap-1"
              data-testid="jeregul-csv-template"
              title="Télécharger un fichier CSV modèle pré-rempli (colonnes reconnues automatiquement)"
            >
              <Download className="h-3 w-3" /> Modèle CSV
            </button>
          </div>
          <div>
            <Label htmlFor="date" className="text-xs">Journée</Label>
            <Input id="date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-44" data-testid="jeregul-date" />
          </div>
        </div>
      </div>

      <Card className="p-4 rounded-md shadow-none">
        <form onSubmit={addRide} className="grid gap-3 md:grid-cols-12 items-end">
          <div className="md:col-span-4 relative">
            <Label className="text-xs">Patient / Client</Label>
            <Input
              value={query}
              ref={patientRef}
              data-testid="jeregul-patient-autocomplete"
              placeholder="Tapez les 1res lettres…"
              onChange={(e) => { setQuery(e.target.value); setPatient(null); }}
              onFocus={() => matches.length && setShowMatches(true)}
              className="mt-1"
            />
            {showMatches && matches.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-56 overflow-auto" data-testid="jeregul-matches">
                {matches.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => pickPatient(p)}
                    className="w-full text-left px-3 py-2 hover:bg-secondary text-sm flex items-center justify-between"
                    data-testid={`patient-option-${p.id}`}
                  >
                    <span>{p.prenom} {p.nom}</span>
                    <Badge variant="outline" className="text-[10px]">{p.type === "prive" ? "Privé" : "Sanitaire"}</Badge>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <Label className="text-xs">{sens === "retour" ? "Fin de soin" : "Heure"}</Label>
            <Input ref={timeRef} type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1" data-testid="jeregul-time" />
            {sens === "retour" && <span className="block text-[10px] text-muted-foreground mt-0.5">PEC au plus tôt après la fin de soin</span>}
          </div>

          <div className="md:col-span-2">
            <Label className="text-xs">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1" data-testid="jeregul-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">{type !== "Taxi" ? "Établissement" : "Adresse de destination"}</Label>
              {type !== "Taxi" && (
                <button type="button" onClick={() => setShowEstab(true)} className="text-[11px] text-primary hover:underline" data-testid="jeregul-manage-estab">+ Ajouter / Rechercher</button>
              )}
            </div>
            {type !== "Taxi" ? (
              <Select value={destIdx} onValueChange={setDestIdx}>
                <SelectTrigger className="mt-1" data-testid="jeregul-dest"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {estabs.map((d, i) => <SelectItem key={`${d.label}-${i}`} value={String(i)}>{d.favorite ? "★ " : ""}{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <div className="mt-1 flex items-start gap-1" data-testid="jeregul-dest-address">
                <div className="flex-1 min-w-0">
                  <AddressAutocomplete
                    value={destQuery}
                    onChange={(txt) => { setDestQuery(txt); setDestAddr(null); }}
                    onSelect={(r) => { setDestAddr({ label: r.label, address: r.label, lat: r.lat, lon: r.lon }); setDestQuery(r.label); }}
                    placeholder="Adresse de destination (sans limite de km)"
                    testid="jeregul-dest-input"
                  />
                </div>
                <SaveAsPlaceButton
                  address={destAddr?.address || destQuery}
                  lat={destAddr?.lat}
                  lon={destAddr?.lon}
                  testid="jeregul-dest-save"
                />
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <Label className="text-xs">Sens du trajet</Label>
            <div className="mt-1 grid grid-cols-3 gap-1 rounded-md border border-border p-0.5" data-testid="jeregul-sens">
              <button
                type="button"
                onClick={() => setSens("aller")}
                title="Aller : domicile → établissement"
                className={`text-xs rounded px-1.5 py-1.5 font-medium transition-colors ${sens === "aller" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
                data-testid="jeregul-sens-aller"
              >Aller</button>
              <button
                type="button"
                onClick={() => setSens("retour")}
                title="Retour : établissement → domicile"
                className={`text-xs rounded px-1.5 py-1.5 font-medium transition-colors ${sens === "retour" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
                data-testid="jeregul-sens-retour"
              >Retour</button>
              <button
                type="button"
                onClick={() => { setSens("aller-retour"); setReturnTime((t) => t || time); }}
                title="Aller-retour : crée les deux courses d'un coup"
                className={`text-xs rounded px-1.5 py-1.5 font-medium transition-colors ${sens === "aller-retour" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
                data-testid="jeregul-sens-aller-retour"
              >A/R</button>
            </div>
          </div>

          <div className="md:col-span-12 flex flex-wrap items-center gap-3 border-t border-border pt-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="jeregul-serie-toggle">
              <Checkbox checked={serie} onCheckedChange={(v) => setSerie(!!v)} data-testid="jeregul-serie-check" />
              <Repeat className="h-3.5 w-3.5 text-primary" /> Course en série (RDV périodiques)
            </label>
            {serie && (
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label className="text-xs whitespace-nowrap">Récurrence</Label>
                  <Select value={serieFreq} onValueChange={setSerieFreq}>
                    <SelectTrigger className="w-40 h-9" data-testid="jeregul-serie-freq"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Tous les jours</SelectItem>
                      <SelectItem value="weekdays">Jours ouvrés (lun-ven)</SelectItem>
                      <SelectItem value="weekly">Toutes les semaines</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs whitespace-nowrap">Nb de RDV</Label>
                  <Input type="number" min={1} max={60} value={serieCount}
                    onChange={(e) => setSerieCount(Number(e.target.value) || 1)}
                    className="w-20 h-9" data-testid="jeregul-serie-count" />
                </div>
                <span className="text-xs text-muted-foreground">à partir du {new Date(date).toLocaleDateString("fr-FR")}</span>
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="jeregul-serie-regul-toggle">
                  <Checkbox checked={serieRegul} onCheckedChange={(v) => setSerieRegul(!!v)} data-testid="jeregul-serie-regul-check" />
                  <Zap className="h-3.5 w-3.5 text-accent-foreground" /> Réguler chaque date après création
                </label>
              </div>
            )}
          </div>

          <div className="md:col-span-12 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              {sens === "aller-retour" && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs whitespace-nowrap">Fin de soin (retour)</Label>
                  <Input type="time" value={returnTime} onChange={(e) => setReturnTime(e.target.value)} className="w-28" data-testid="jeregul-return-time" />
                  <span className="text-[11px] text-muted-foreground">PEC au plus tôt, attente max 60 min</span>
                </div>
              )}
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {patient
                  ? (() => {
                      const dObj = type !== "Taxi" ? (estabs[Number(destIdx)] || estabs[0]) : destAddr;
                      const dLbl = dObj?.label || dObj?.address || "destination ?";
                      return sens === "aller-retour"
                        ? `${patient.address} ⇄ ${dLbl}`
                        : sens === "aller"
                          ? `${patient.address} → ${dLbl}`
                          : `${dLbl} → ${patient.address}`;
                    })()
                  : "Sélectionnez un patient"}
              </p>
            </div>
            <Button type="submit" data-testid="jeregul-add" disabled={!patient}>
              {serie ? `Ajouter la série (${buildDates().length})` : "Ajouter (Entrée)"}
            </Button>
          </div>
        </form>
      </Card>

      {regulResult?.surcharge > 0 && tribus.length > 0 && (
        <Card className="p-4 rounded-md shadow-none border-accent bg-accent/10" data-testid="regul-suggestion">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-medium flex items-center gap-2">
              <Send className="h-4 w-4 text-primary" />
              Dispatch IA : {regulResult.surcharge} course(s) en surcharge. Les pousser sur une bourse de tribu (anonymisé) ?
            </p>
            <div className="flex flex-wrap gap-2">
              {tribus.map((t) => (
                <Button key={t.id} size="sm" onClick={() => pushAllSurcharge(t.id)} data-testid={`regul-push-${t.id}`}>
                  Pousser sur {t.name}
                </Button>
              ))}
            </div>
          </div>
        </Card>
      )}

      <RegulReport report={report} />

      <ContextMenu>
        <ContextMenuTrigger asChild>
          <Card className="p-0 rounded-md shadow-none overflow-hidden" data-testid="jeregul-table">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="font-display font-semibold">Bac d'attente — {rides.length} course(s) · {pending.length} non affectée(s)</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Heure</TableHead>
              <TableHead>Patient</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Trajet</TableHead>
              <TableHead>Km</TableHead>
              <TableHead>Affectation</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rides.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Aucune course. Ajoutez-en ci-dessus.</TableCell></TableRow>
            )}
            {rides.map((r) => (
              <ContextMenu key={r.id}>
                <ContextMenuTrigger asChild>
                  <TableRow data-testid={`ride-row-${r.id}`}>
                    <TableCell className="font-medium">{r.time}</TableCell>
                    <TableCell>{r.patient_name}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{r.vehicle_type || r.type}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate">{r.pickup_address} → {r.dropoff_address}</TableCell>
                    <TableCell className="text-sm">{r.distance_km}</TableCell>
                    <TableCell>
                      {r.assigned_vehicle_id
                        ? <span className="inline-flex items-center gap-1">
                            <EntityLink type="vehicle" id={r.assigned_vehicle_id} testid={`ride-vehicle-link-${r.id}`}>
                              <Badge className={STATUS_META[r.status]?.cls}>{r.assigned_vehicle_label}</Badge>
                            </EntityLink>
                            {r.shared && <Badge variant="outline" className="border-primary text-primary text-[10px]" title="Transport groupé">Groupé ×{r.group_size}</Badge>}
                          </span>
                        : r.on_bourse
                          ? <Badge variant="outline" className="border-accent bg-accent/20 text-amber-700 dark:text-amber-300">Sur la bourse</Badge>
                          : r.surcharge
                            ? <Badge variant="destructive">Surcharge</Badge>
                            : <span className="text-xs text-muted-foreground">En attente</span>}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {false && r.surcharge && !r.assigned_vehicle_id && !r.on_bourse && tribus.length > 0 && (
                        <Button size="icon" variant="ghost" title="Pousser sur la bourse" onClick={() => setPushRide(r)} data-testid={`ride-push-${r.id}`}>
                          <Send className="h-4 w-4 text-primary" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" title="Modifier la course" onClick={() => openEdit(r)} data-testid={`ride-edit-${r.id}`}>
                        <Pencil className="h-4 w-4 text-muted-foreground" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => removeRide(r.id)} data-testid={`ride-delete-${r.id}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                </ContextMenuTrigger>
                <ContextMenuContent className="w-56" data-testid={`ride-context-${r.id}`}>
                  <ContextMenuLabel className="truncate">{r.time} · {r.patient_name}</ContextMenuLabel>
                  <ContextMenuSeparator />
                  <ContextMenuItem onSelect={() => focusForm()} data-testid={`ctx-add-${r.id}`}>
                    <Plus className="h-4 w-4 mr-2" /> Ajouter une course
                  </ContextMenuItem>
                  <ContextMenuItem onSelect={() => openEdit(r)} data-testid={`ctx-edit-${r.id}`}>
                    <Pencil className="h-4 w-4 mr-2" /> Modifier cette course
                  </ContextMenuItem>
                  <ContextMenuItem onSelect={() => removeRide(r.id)} className="text-destructive focus:text-destructive" data-testid={`ctx-remove-${r.id}`}>
                    <Trash2 className="h-4 w-4 mr-2" /> Retirer cette course
                  </ContextMenuItem>
                  {false && r.surcharge && !r.assigned_vehicle_id && !r.on_bourse && tribus.length > 0 && (
                    <ContextMenuItem onSelect={() => setPushRide(r)} data-testid={`ctx-push-${r.id}`}>
                      <Send className="h-4 w-4 mr-2" /> Pousser sur la bourse
                    </ContextMenuItem>
                  )}
                  <ContextMenuSeparator />
                  <ContextMenuItem onSelect={() => runRegul(false)} data-testid={`ctx-regul-${r.id}`}>
                    <Zap className="h-4 w-4 mr-2" /> Lancer la régul
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            ))}
          </TableBody>
        </Table>
          </Card>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-56" data-testid="jeregul-bac-context">
          <ContextMenuLabel>Planning du {new Date(date).toLocaleDateString("fr-FR")}</ContextMenuLabel>
          <ContextMenuSeparator />
          <ContextMenuItem onSelect={() => focusForm()} data-testid="ctx-bac-add">
            <Plus className="h-4 w-4 mr-2" /> Ajouter une course
          </ContextMenuItem>
          <ContextMenuItem onSelect={() => runRegul(false)} disabled={rides.length === 0} data-testid="ctx-bac-regul">
            <Zap className="h-4 w-4 mr-2" /> Lancer la régul
          </ContextMenuItem>
          <ContextMenuItem onSelect={() => runRegul(true)} disabled={rides.length === 0} data-testid="ctx-bac-reset">
            <RefreshCw className="h-4 w-4 mr-2" /> Tout recalculer (remise à zéro)
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      <div className="sticky bottom-4 flex flex-col items-center gap-2">
        {drivers.length > 0 && (
          <div
            className={`text-sm rounded-full px-4 py-1.5 border flex items-center gap-2 shadow-sm backdrop-blur ${understaffed ? "bg-red-500/15 border-red-500/40 text-red-700 dark:text-red-300" : "bg-green-500/15 border-green-500/40 text-green-700 dark:text-green-400"}`}
            data-testid="jeregul-staffing"
          >
            {understaffed ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
            {onRest} en repos · {availableDrivers} chauffeur(s) dispo / {vehCount} véhicule(s) — {understaffed ? "effectif insuffisant" : "effectif suffisant"}
          </div>
        )}
        <Button
          size="lg"
          onClick={() => runRegul(false)}
          disabled={regulating || rides.length === 0}
          data-testid="jeregul-regul-button"
          className="h-16 px-12 text-lg font-display font-extrabold rounded-full shadow-xl bg-accent text-accent-foreground hover:bg-accent/90"
        >
          {regulating ? <Loader2 className="h-6 w-6 mr-2 animate-spin" /> : <Zap className="h-6 w-6 mr-2" />}
          RÉGUL
        </Button>
        <button
          type="button"
          onClick={() => runRegul(true)}
          disabled={regulating || rides.length === 0}
          data-testid="jeregul-regul-reset"
          className="text-xs text-muted-foreground underline-offset-2 hover:underline disabled:opacity-50 flex items-center gap-1"
          title="Efface toutes les affectations du jour et redispatche entièrement (corrige les chevauchements résiduels)"
        >
          <RefreshCw className="h-3 w-3" /> Tout recalculer (remise à zéro)
        </button>
        <button
          type="button"
          onClick={runAiAnalysis}
          disabled={aiBusy}
          data-testid="jeregul-ai-analyze"
          className="text-xs text-primary underline-offset-2 hover:underline disabled:opacity-50 flex items-center gap-1"
          title="Claude (IA économique) relit le planning régulé et propose des optimisations : regroupements manqués, rééquilibrage, risques"
        >
          {aiBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
          {aiBusy ? "Analyse IA en cours…" : "Analyse IA du planning"}
        </button>
      </div>

      <Dialog open={!!editRide} onOpenChange={(o) => !o && setEditRide(null)}>
        <DialogContent className="max-w-lg" data-testid="jeregul-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modifier la course</DialogTitle>
            <DialogDescription>La course repart en attente de régulation après modification.</DialogDescription>
          </DialogHeader>
          {editRide && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Heure</Label>
                  <Input type="time" value={editRide.time} onChange={(e) => setEditRide({ ...editRide, time: e.target.value })} className="mt-1" data-testid="edit-time" />
                </div>
                <div>
                  <Label className="text-xs">Catégorie de véhicule</Label>
                  <Select value={editRide.vehicle_type} onValueChange={(v) => setEditRide({ ...editRide, vehicle_type: v })}>
                    <SelectTrigger className="mt-1" data-testid="edit-vehicle-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Patient / Client</Label>
                <Input value={editRide.patient_name} onChange={(e) => setEditRide({ ...editRide, patient_name: e.target.value })} className="mt-1" data-testid="edit-patient" />
              </div>
              <div>
                <Label className="text-xs">Adresse de prise en charge</Label>
                <div className="mt-1 flex items-start gap-1">
                  <div className="flex-1 min-w-0">
                    <AddressAutocomplete value={editRide.pickupQuery}
                      onChange={(txt) => setEditRide({ ...editRide, pickupQuery: txt })}
                      onSelect={(r) => setEditRide({ ...editRide, pickupQuery: r.label, pickup_address: r.label, pickup_lat: r.lat, pickup_lon: r.lon })}
                      placeholder="Adresse de départ" testid="edit-pickup" />
                  </div>
                  <SaveAsPlaceButton
                    address={editRide.pickup_address || editRide.pickupQuery}
                    lat={editRide.pickup_lat} lon={editRide.pickup_lon}
                    testid="edit-pickup-save"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Adresse de destination</Label>
                <div className="mt-1 flex items-start gap-1">
                  <div className="flex-1 min-w-0">
                    <AddressAutocomplete value={editRide.dropoffQuery}
                      onChange={(txt) => setEditRide({ ...editRide, dropoffQuery: txt })}
                      onSelect={(r) => setEditRide({ ...editRide, dropoffQuery: r.label, dropoff_address: r.label, dropoff_lat: r.lat, dropoff_lon: r.lon })}
                      placeholder="Adresse d'arrivée" testid="edit-dropoff" />
                  </div>
                  <SaveAsPlaceButton
                    address={editRide.dropoff_address || editRide.dropoffQuery}
                    lat={editRide.dropoff_lat} lon={editRide.dropoff_lon}
                    testid="edit-dropoff-save"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditRide(null)} data-testid="edit-cancel">Annuler</Button>
                <Button onClick={saveEdit} data-testid="edit-save">Enregistrer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showEstab} onOpenChange={(o) => { if (!o) { setShowEstab(false); loadEstabs(); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto" data-testid="jeregul-estab-dialog">
          <DialogHeader>
            <DialogTitle>Établissements de santé</DialogTitle>
            <DialogDescription>Recherchez les établissements autour de vous ou ajoutez-les manuellement. Ils apparaîtront dans le menu « Établissement ».</DialogDescription>
          </DialogHeader>
          <EstablishmentManager onChange={(list) => {
            const mapped = (list || []).map((e) => ({ label: e.name, lat: e.lat, lon: e.lon, address: e.address || e.name, favorite: !!e.favorite }));
            const finalList = mapped.length ? mapped : DESTINATIONS;
            setEstabs(finalList);
            const favIdx = finalList.findIndex((e) => e.favorite);
            if (favIdx >= 0) setDestIdx(String(favIdx));
          }} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!importJob} onOpenChange={(o) => !o && setImportJob(null)}>
        <DialogContent className="max-w-4xl" data-testid="jeregul-import-preview">
          <DialogHeader>
            <DialogTitle>Vérification avant import — {importJob?.proposed?.length || 0} course(s) détectée(s)</DialogTitle>
            <DialogDescription>
              {importJob?.method === "csv" ? "Colonnes du fichier reconnues automatiquement (sans IA)." : "Courses extraites par IA (Claude Haiku)."} Décochez les lignes à exclure puis validez : les courses seront créées puis régulées.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-auto border border-border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <Checkbox checked={importSel.length === (importJob?.proposed?.length || 0)}
                      onCheckedChange={(v) => setImportSel(v ? (importJob?.proposed || []).map((_, i) => i) : [])}
                      data-testid="import-preview-check-all" />
                  </TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Heure</TableHead>
                  <TableHead>Fin de soin</TableHead>
                  <TableHead>Trajet</TableHead>
                  <TableHead>Cat.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(importJob?.proposed || []).map((r, i) => (
                  <TableRow key={`${i}-${r.patient_name || ""}`} data-testid={`import-preview-row-${i}`} className={importSel.includes(i) ? "" : "opacity-45"}>
                    <TableCell>
                      <Checkbox checked={importSel.includes(i)}
                        onCheckedChange={(v) => setImportSel((s) => v ? [...s, i] : s.filter((x) => x !== i))}
                        data-testid={`import-preview-check-${i}`} />
                    </TableCell>
                    <TableCell className="font-medium whitespace-nowrap">
                      {r.patient_name}
                      {r.patient_id
                        ? <Badge variant="outline" className="ml-1.5 text-[10px] border-green-500 text-green-700 dark:text-green-400" data-testid={`import-preview-matched-${i}`}>Fiche liée</Badge>
                        : (
                          <button type="button" onClick={() => createFiche(i)}
                            className="ml-1.5 text-[10px] text-primary hover:underline"
                            title="Créer une fiche patient à partir de cette ligne (nom, téléphone, adresse domicile)"
                            data-testid={`import-preview-create-patient-${i}`}>
                            + Créer la fiche
                          </button>
                        )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{r.date}</TableCell>
                    <TableCell>{r.time}</TableCell>
                    <TableCell>{r.care_end || "—"}</TableCell>
                    <TableCell className="text-xs max-w-[280px] truncate" title={`${r.pickup_address} → ${r.dropoff_address}`}>
                      {r.pickup_address} → {r.dropoff_address}
                    </TableCell>
                    <TableCell><Badge variant="secondary">{r.vehicle_type || r.type}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {!!importJob?.skipped?.length && (
            <p className="text-xs text-muted-foreground" data-testid="import-preview-skipped">
              {importJob.skipped.length} ligne(s) non importable(s) : {importJob.skipped.slice(0, 3).map((s) => `${s.patient_name} (${s.reason})`).join(" · ")}{importJob.skipped.length > 3 ? "…" : ""}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setImportJob(null)} data-testid="import-preview-cancel">Annuler</Button>
            <Button onClick={confirmImport} disabled={confirming || importSel.length === 0} className="gap-2" data-testid="import-preview-confirm">
              {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Valider {importSel.length} course(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pushRide} onOpenChange={(o) => !o && setPushRide(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Pousser sur une Tribu</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Le confrère verra une version anonymisée jusqu'à acceptation.</p>
          <div className="space-y-2">
            {tribus.map((t) => (
              <Button key={t.id} variant="outline" className="w-full justify-start" onClick={() => pushToBourse(t.id)} data-testid={`push-tribu-${t.id}`}>
                {t.name}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
